// @flow
import i18n from 'app/common/lib/i18n';
import localeUtils from 'app/common/lib/locale';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import urlUtil from 'app/common/lib/urlUtil';
import { safeFormat } from 'app/common/lib/StringUtil';
import {
  MIN_DURATION,
  MAX_DURATION,
  MAX_NAME_LENGTH,
  MIN_BUDGET_IN_MICROCURRENCY,
  ONE_MILLION,
} from 'app/common/react/components/partner/QuickPromote/lib/constants';

type ValidatorInputMapType = {
  bid?: number,
  budget?: string,
  currency_code: string,
  destination_url?: string,
  duration?: string,
  locale: string,
  name?: string,
  search_keywords?: Array<string>,
};

type ValidatorResultType = {
  bid?: string,
  budget?: string,
  destination_url?: string,
  duration?: string,
  name?: string,
  search_keywords?: string,
};

// To make sure it's compatible with all browsers
export const isInteger =
  Number.isInteger ||
  function(value) {
    return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
  };

function numberFormat(money_in_micros, currency_code, locale) {
  return localeUtils.numberFormat(
    locale,
    money_in_micros / ONE_MILLION /* value to be converted */,
    2 /* Max cents fraction */,
    2 /* Min cents fraction */,
    'currency',
    currency_code /* i.e. '$', '€', etc. */,
  );
}

export function validateName(name?: string): ?string {
  const adName = name && name.trim();
  let error;
  if (!adName) {
    error = i18n._(
      'Give your campaign a name',
      '[m10n] ad name text field missing error message in quick edit',
    );
  } else if (adName.length > MAX_NAME_LENGTH) {
    error = i18n._(
      '64 characters or less, please',
      '[m10n] ad name text field over length error message in quick edit',
    );
  }
  return error;
}

export function validateUrl(url: ?string): ?string {
  const error = i18n._(
    'Add a valid destination link',
    '[m10n] ad url text field error message in quick edit',
  );

  return url && url.trim().length > 0 && urlUtil.validateUrl(url) ? undefined : error;
}

export function validateBudget(budget?: string, currency_code: string, locale: string): ?string {
  let error;
  if (!budget) {
    error = i18n._('Add a budget', '[m10n] missing budget error message on quick promote form');
  } else if (isNaN(Number(budget))) {
    error = i18n._(
      "That's not a valid budget",
      '[m10n] invalid budget error message on quick promote form',
    );
  } else if (Number(budget) * ONE_MILLION < MIN_BUDGET_IN_MICROCURRENCY) {
    const minimumAllowedBudget = numberFormat(MIN_BUDGET_IN_MICROCURRENCY, currency_code, locale);
    const errorMessage = safeFormat(
      i18n._(
        'Minimum budget is {{ minimumAllowedBudget }}',
        '[m10n] The placeholder is the equivalent of "$0.10"',
      ),
      {
        minimumAllowedBudget,
      },
    );
    error = errorMessage;
  }
  return error;
}

export const validateDuration = (duration?: string): ?string => {
  let error;
  if (!duration) {
    error = i18n._(
      'Minimum duration is 1 day',
      '[m10n] less than minimum duration error message for duration field',
    );
  } else if (!/^([1-9]\d*)$/.test(duration)) {
    error = i18n._(
      'Oops! Enter a valid number',
      '[m10n] invalid number input error message for duration field',
    );
  } else if (Number(duration) < MIN_DURATION) {
    error = i18n._(
      'Minimum duration is 1 day',
      '[m10n] less than minimum duration error message for duration field',
    );
  } else if (Number(duration) > MAX_DURATION) {
    error = i18n._(
      'Maximum duration is 365 days',
      '[m10n] over maximum duration error message for duration field',
    );
  }

  return error;
};

export const validateTargeting = (targeting?: ?Array<any>): ?string => {
  let error;
  if (!targeting || targeting.length === 0) {
    error = i18n._(
      'Add at least one',
      '[m10n]"One" in this instance meaning a search keyword. i.e., "inspiring"',
    );
  }
  return error;
};

/**
 * redux-form validation for the QuickPromoteForm
 * @param  {ValidatorInputMapType} values form field values
 * @return {ValidatorResultType} map of errors, empty if successfully validated
 */
const genericValidator = ({
  budget,
  currency_code,
  destination_url,
  duration,
  locale,
  name,
  search_keywords,
}: ValidatorInputMapType = {}): ValidatorResultType => {
  const errors = {};

  // Campaign name
  const nameError = validateName(name);
  if (nameError && typeof nameError === 'string') {
    errors.name = nameError;
  }

  // Campaign destination URL
  const urlError = validateUrl(destination_url);
  if (urlError && typeof urlError === 'string') {
    errors.destination_url = i18n._('Add a link for this Pin');
  }

  // Duration
  const durationError = validateDuration(duration);
  if (durationError && typeof durationError === 'string') {
    errors.duration = durationError;
  }

  // Budget
  const budgetError = validateBudget(budget, currency_code, locale);
  if (budgetError && typeof budgetError === 'string') {
    errors.budget = budgetError;
  }

  // Keywords
  const keywordsError = validateTargeting(search_keywords);
  if (keywordsError && typeof keywordsError === 'string') {
    errors.search_keywords = keywordsError;
  }

  const fieldsWithErrors = Object.keys(errors);
  fieldsWithErrors.map((f) => trackRegisterAction(`QuickPromoteModal.invalidField.${f}`));

  return errors;
};

const validator = ({
  budget,
  currency_code,
  destination_url,
  duration,
  locale,
  name,
  search_keywords,
}: ValidatorInputMapType = {}): ValidatorResultType =>
  genericValidator({
    budget,
    currency_code,
    destination_url,
    duration,
    locale,
    name,
    search_keywords,
    shouldValidateKeywords: true,
  });

export default validator;
